/**
 * Error Utilities
 * Centralized error handling for API calls
 */

export const ERROR_TYPES = {
  NETWORK: 'network',
  SERVER: 'server',
  VALIDATION: 'validation',
  UNKNOWN: 'unknown',
};

/**
 * Determines the type of an Axios error.
 * - Network errors: no response (offline, DNS failure, timeout)
 * - Server errors: 5xx status codes
 * - Validation errors: 4xx status codes (business logic / validation)
 * - Unknown: everything else
 */
export const getErrorType = (error) => {
  if (!error) return ERROR_TYPES.UNKNOWN;

  // Network error — Axios sets code to ERR_NETWORK or response is undefined
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) {
    return ERROR_TYPES.NETWORK;
  }

  const status = error.response?.status;
  if (status >= 500) return ERROR_TYPES.SERVER;
  if (status >= 400) return ERROR_TYPES.VALIDATION;

  return ERROR_TYPES.UNKNOWN;
};

/**
 * Checks if an error is a network connectivity issue.
 */
export const isNetworkError = (error) => getErrorType(error) === ERROR_TYPES.NETWORK;

/**
 * Extracts a user-friendly error message from an Axios error.
 * @param {Object} error - Axios error object
 * @param {string} fallbackMessage - Fallback message for non-network errors
 * @returns {{ type: string, message: string }}
 */
export const getApiErrorMessage = (error, fallbackMessage = 'errors.somethingWentWrong') => {
  const type = getErrorType(error);

  switch (type) {
    case ERROR_TYPES.NETWORK:
      return {
        type,
        message: 'errors.networkError',
      };
    case ERROR_TYPES.SERVER:
      return {
        type,
        message: 'errors.serverError',
      };
    case ERROR_TYPES.VALIDATION:
      return {
        type,
        message: error.response?.data?.error || error.response?.data?.message || error.message || fallbackMessage,
      };
    default:
      return {
        type,
        message: error.message || fallbackMessage,
      };
  }
};
